import { sharedCtx } from '@pharmsnap/shared/context'
import { ElInput } from '@pharmsnap/shared/element-ui'
import { showSingleToast } from '@pharmsnap/shared/utils'
import { computed, defineComponent, getCurrentInstance, PropType, ref, watch } from '@vue/composition-api'
import { GDialog } from '../../ui/GDialog/GDialog'
import $style from './BSavedSearchDialog.module.scss'
import cn from './locales/cn.json'
import en from './locales/en.json'

const SAVED_SEARCH_EXIST_ERROR_CODE = 130151

export const BSavedSearchDialog = defineComponent({
  name: 'BSavedSearchDialog',
  model: {
    prop: 'visible',
    event: 'input',
  },
  i18n: {
    messages: { cn, en },
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    queryId: {
      type: String as PropType<string | undefined>,
    },
    queryText: {
      type: String,
    },
    filterText: {
      type: String,
    },
  },
  setup(props, ctx) {
    const ins = getCurrentInstance()

    const name = ref('')
    const queryString = ref('')
    const loading = ref(false)
    const saving = ref(false)

    const confirmDisabled = computed(() => loading.value || saving.value || !props.queryId || !name.value.trim())

    const hasPreviewText = computed(() => !!props.queryText || !!props.filterText)

    function getPreviewText() {
      const previewTextList = []
      if (props.queryText) {
        previewTextList.push(props.queryText)
      }
      if (props.filterText) {
        previewTextList.push(`${ins?.proxy.$tc('BSavedSearchDialog.filterPrefix')}${props.filterText}`)
      }
      return previewTextList.join('\n')
    }

    function setDefaultName(value: string) {
      name.value = value.trim()
    }

    async function loadPreview() {
      if (hasPreviewText.value) {
        queryString.value = getPreviewText()
        loading.value = false
        return
      }

      if (!props.visible || !props.queryId) {
        queryString.value = ''
        return
      }

      loading.value = true
      const rt = await sharedCtx.service.savedSearch.preview({ query_id: props.queryId })
      if (rt.success) {
        queryString.value = rt.data.query_string || ''
      }
      loading.value = false
    }

    function closeDialog() {
      ctx.emit('input', false)
    }

    async function save() {
      if (confirmDisabled.value || !props.queryId) return

      saving.value = true
      const rt = await sharedCtx.service.savedSearch.create({
        name: name.value.trim(),
        query_id: props.queryId,
      })
      saving.value = false

      if (rt.success) {
        showSingleToast({
          type: 'success',
          message: ins?.proxy.$t('BSavedSearchDialog.saveSuccess') as string,
        })
        ctx.emit('saved', rt.data)
        closeDialog()
      } else if (rt.data?.numeric_error_code === SAVED_SEARCH_EXIST_ERROR_CODE) {
        showSingleToast({
          type: 'error',
          message: ins?.proxy.$t('BSavedSearchDialog.duplicateError') as string,
        })
      }
    }

    watch(
      () => [props.visible, props.queryId, props.queryText, props.filterText],
      async () => {
        if (props.visible) {
          name.value = ''
          await loadPreview()
          setDefaultName(queryString.value)
        }
      },
      { immediate: true }
    )

    return {
      name,
      queryString,
      loading,
      saving,
      confirmDisabled,
      closeDialog,
      save,
    }
  },
  render() {
    return (
      <GDialog
        width="600px"
        visible={this.visible}
        title={this.$tc('BSavedSearchDialog.title')}
        confirmText={this.$tc('BSavedSearchDialog.save')}
        cancelText={this.$tc('BSavedSearchDialog.cancel')}
        confirmDisabled={this.confirmDisabled}
        confirmLoading={this.saving}
        onCancel={this.closeDialog}
        onClose={this.closeDialog}
        onConfirm={this.save}
      >
        <div class={$style.form}>
          <div class={$style.row}>
            <div class={$style.label}>
              <span class={$style.required}>*</span>
              {this.$tc('BSavedSearchDialog.name')}
            </div>
            <div class={$style.field}>
              <ElInput vModel={this.name} placeholder={this.$tc('BSavedSearchDialog.namePlaceholder')} disabled={this.saving}></ElInput>
            </div>
          </div>
          <div class={$style.row}>
            <div class={$style.label}>{this.$tc('BSavedSearchDialog.queryString')}</div>
            <div class={$style.field}>
              <div class={$style.queryPreview}>
                {this.loading ? (
                  <span class={$style.emptyText}>{this.$tc('BSavedSearchDialog.loading')}</span>
                ) : (
                  this.queryString || <span class={$style.emptyText}>{this.$tc('BSavedSearchDialog.emptyQuery')}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </GDialog>
    )
  },
})
